import {
  ActionIcon,
  Button,
  Group,
  List,
  Modal,
  Stack,
  Text,
  ThemeIcon,
} from "@mantine/core";
import { showNotification, updateNotification } from "@mantine/notifications";
import { IconArchiveOff } from "@tabler/icons-react";
import {
  getNotificationByResultType,
  loadingInfoNotification,
} from "beitary-shared";
import { useSubmitState } from "hooks/useSubmitState";
import { useState } from "react";
import { useTranslation } from "react-i18next";

export interface DearchiveConfirmationModalProps<T> {
  f: Function;
  item: T;
  button?: boolean;
}

export const DearchiveConfirmationModal = <
  T extends { id: string; name: string }
>({
  f,
  item,
  button,
}: DearchiveConfirmationModalProps<T>) => {
  const { t } = useTranslation();
  const [submitState, setSubmitState] = useSubmitState();
  const [opened, setOpened] = useState(false);

  const dearchiveItem = async () => {
    const notificationId = `dearchive-item-${item.id}`;
    showNotification(
      loadingInfoNotification({
        id: notificationId,
        message: t("WAITING_FOR_SERVER_RESPONSE"),
        title: t("DEARCHIVE_ITEM"),
      })
    );
    setSubmitState("pending-response");
    const result = await f(item.id, item);
    setSubmitState(result.type);

    updateNotification({
      ...getNotificationByResultType(result.type)({
        message: t(result.message),
      }),
      id: notificationId,
    });
  };

  const confirm = () => {
    dearchiveItem();
    setOpened(false);
  };

  return (
    <>
      <Modal
        opened={opened}
        centered
        onClose={() => setOpened(false)}
        closeOnClickOutside={false}
        overlayProps={{
          color: "white",
          opacity: 0.55,
          blur: 3,
        }}
      >
        <Stack pb="xl" align="center" spacing={0}>
          <ThemeIcon size={140} color="cyan.4" radius={70} variant="light">
            <IconArchiveOff size={100} />
          </ThemeIcon>
          <Text weight="bold" mt="xl">
            {t("YOU_ARE_ABOUT_TO_DEARCHIVE_AN_ITEM")}
          </Text>
          <List>
            <List.Item>{item.name}</List.Item>
          </List>
          <Text mt="xl">
            {t("DEARCHIVED_ITEMS_WILL_NOT_SHOW_UP_AGAIN_TO_USERS")}
          </Text>
        </Stack>
        <Group mt="xl" position="right">
          <Button onClick={confirm}>{t("DEARCHIVE")}</Button>
          <Button variant="outline" onClick={() => setOpened(false)}>
            {t("CANCEL")}
          </Button>
        </Group>
      </Modal>
      {button ? (
        <Button
          onClick={() => setOpened(true)}
          loading={submitState === "pending-response"}
          leftIcon={<IconArchiveOff size={18} />}
          variant="outline"
        >
          {t("DEARCHIVE")}
        </Button>
      ) : (
        <ActionIcon
          onClick={() => setOpened(true)}
          loading={submitState === "pending-response"}
        >
          <IconArchiveOff size={18} />
        </ActionIcon>
      )}
    </>
  );
};
